import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { COOKIE_CONFIG, cookieHelpers } from '../config/cookies';

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [cookiePreferences, setCookiePreferences] = useState(COOKIE_CONFIG.defaultPreferences);

  useEffect(() => {
    // Only check for consent if banner is enabled
    if (!COOKIE_CONFIG.enabled) return;

    // Check if user has already made a choice
    const existingConsent = localStorage.getItem(COOKIE_CONFIG.cookieName);
    if (!existingConsent) {
      setShowBanner(true);
    }
  }, []);

  const saveConsentPreferences = (preferences) => {
    const consentData = {
      preferences,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem(COOKIE_CONFIG.cookieName, JSON.stringify(consentData));
    setShowBanner(false);

    // Initialize tools based on preferences
    if (preferences.analytics) {
      cookieHelpers.initializeAnalytics();
    }
    
    if (preferences.marketing) {
      cookieHelpers.initializeMarketing();
    }
    
    if (preferences.performance) {
      cookieHelpers.initializePerformance();
    }
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      performance: true
    };
    saveConsentPreferences(allAccepted);
  };

  const acceptNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
      performance: false
    };
    saveConsentPreferences(necessaryOnly);
  };

  const handleCustomPreferences = () => {
    saveConsentPreferences(cookiePreferences);
  };

  const updatePreference = (type, value) => {
    setCookiePreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  // Don't render anything if banner is disabled or not shown
  if (!COOKIE_CONFIG.enabled || !showBanner) {
    return null;
  }

  return (
    <AnimatePresence>
      {showBanner && (
        <motion.div
          className="cookie-banner-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="cookie-banner"
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
            <div className="cookie-banner-content">
              <div className="cookie-banner-header">
                <h3>🍪 We Value Your Privacy</h3>
              </div>
              
              <div className="cookie-banner-text">
                <p>
                  We use cookies to enhance your experience on PawPrints. Some cookies are essential for the website to function, 
                  while others help us understand how you use our site and improve our services.
                </p>
              </div>

              <div className="cookie-preferences">
                <div className="cookie-preference-item">
                  <label className="cookie-preference-label">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.necessary}
                      disabled={true}
                    />
                    <span className="cookie-preference-name">Necessary Cookies</span>
                    <span className="cookie-preference-desc">Required for basic website functionality</span>
                  </label>
                </div>

                <div className="cookie-preference-item">
                  <label className="cookie-preference-label">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.analytics}
                      onChange={(e) => updatePreference('analytics', e.target.checked)}
                    />
                    <span className="cookie-preference-name">Analytics Cookies</span>
                    <span className="cookie-preference-desc">Help us understand how visitors interact with our website</span>
                  </label>
                </div>

                <div className="cookie-preference-item">
                  <label className="cookie-preference-label">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.performance}
                      onChange={(e) => updatePreference('performance', e.target.checked)}
                    />
                    <span className="cookie-preference-name">Performance Cookies</span>
                    <span className="cookie-preference-desc">Monitor website performance and loading times</span>
                  </label>
                </div>

                <div className="cookie-preference-item">
                  <label className="cookie-preference-label">
                    <input
                      type="checkbox"
                      checked={cookiePreferences.marketing}
                      onChange={(e) => updatePreference('marketing', e.target.checked)}
                    />
                    <span className="cookie-preference-name">Marketing Cookies</span>
                    <span className="cookie-preference-desc">Used to deliver relevant advertisements and track campaigns</span>
                  </label>
                </div>
              </div>

              <div className="cookie-banner-actions">
                <button
                  className="cookie-btn cookie-btn-accept-all"
                  onClick={acceptAll}
                >
                  Accept All
                </button>
                <button
                  className="cookie-btn cookie-btn-necessary"
                  onClick={acceptNecessaryOnly}
                >
                  Necessary Only
                </button>
                <button
                  className="cookie-btn cookie-btn-custom"
                  onClick={handleCustomPreferences}
                >
                  Save Preferences
                </button>
              </div>

              <div className="cookie-banner-links">
                <Link to="/cookies" className="cookie-link">Cookie Policy</Link>
                <span className="cookie-separator">|</span>
                <Link to="/privacy" className="cookie-link">Privacy Policy</Link>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;